import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Card, Badge, Button, Alert, Input, Label } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

function fmtDateTime(iso) { return iso ? new Date(iso).toLocaleString("it-IT") : "—"; }

const SEVERITY_TONE = { CRITICAL: "danger", WARNING: "warning", INFO: "info" };

// ---------- KPI Home ----------

function KpiCard({ label, value, tone = "neutral", testid, sublabel }) {
  return (
    <Card tone={tone} data-testid={testid} style={{ minHeight: 120 }}>
      <Card.Body>
        <div className="muted" style={{ fontSize: ".85rem" }}>{label}</div>
        <div style={{ fontSize: "2.2rem", fontWeight: 700 }}>{value}</div>
        {sublabel && <div className="muted" style={{ fontSize: ".8rem" }}>{sublabel}</div>}
      </Card.Body>
    </Card>
  );
}

export function AdminHomeKpi() {
  const { apiGet } = useAuth();
  const [k, setK] = useState(null);
  const [err, setErr] = useState(null);

  // Feb 2026 — La home Admin è una workspace di CONFIGURAZIONE portale,
  // non una dashboard operativa. I KPI operativi (ordini, spedizioni,
  // consegne, pagamenti) sono responsabilità dei ruoli SALES/LOGISTICS.
  // Manteniamo comunque un piccolo pannello di supporto tecnico con:
  //   - Anomalie CRITICAL aperte
  //   - Email FAILED
  // così l'admin può accorgersi al colpo d'occhio di problemi di sistema.
  useEffect(() => {
    (async () => {
      try { setK(await apiGet("/api/admin/dashboard/kpis")); }
      catch (e) { setErr(e?.message || String(e)); }
    })();
  }, []);

  const QuickBtn = ({ to, label, testid, primary }) => (
    <Link to={to} style={{ textDecoration: "none" }}>
      <Button variant={primary ? "primary" : "secondary"} data-testid={testid}
              style={{ width: "100%", justifyContent: "flex-start" }}>{label}</Button>
    </Link>
  );

  const critical = k?.anomalies_critical ?? 0;
  const failed = k?.outbox_failed ?? 0;
  const hasAlerts = critical > 0 || failed > 0;

  return (
    <Section title="Configurazione Portale"
             subtitle="Workspace amministratore — gestisci utenti, anagrafiche commerciali e regole del portale"
             data-testid="admin-home-config"
             actions={<Badge tone="success">ADMIN</Badge>}>
      {err && <Alert variant="danger">Errore: {err}</Alert>}

      {/* Supporto tecnico compatto (non KPI operativi) */}
      {k && hasAlerts && (
        <Card data-testid="admin-support-alerts" style={{ marginBottom: 16,
              borderLeft: "5px solid #dc2626" }}>
          <Card.Header><strong>⚠️ Supporto tecnico — richiede attenzione</strong></Card.Header>
          <Card.Body>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {critical > 0 && (
                <Link to="/admin/anomalies?severity=CRITICAL"
                      data-testid="admin-alert-anomalies"
                      style={{ textDecoration: "none" }}>
                  <Badge tone="danger">{critical} anomalie CRITICAL</Badge>
                </Link>
              )}
              {failed > 0 && (
                <Link to="/admin/outbox?status=FAILED"
                      data-testid="admin-alert-outbox"
                      style={{ textDecoration: "none" }}>
                  <Badge tone="danger">{failed} email FAILED</Badge>
                </Link>
              )}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* CONFIGURAZIONE — riquadro principale */}
      <div style={{ marginTop: 8, display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}
           data-testid="admin-quick-actions">
        <Card>
          <Card.Header><strong>Configurazione</strong></Card.Header>
          <Card.Body style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <QuickBtn to="/admin/users" label="Utenti" testid="admin-nav-users" primary />
            <QuickBtn to="/admin/entities" label="Configurazioni" testid="admin-nav-entities" />
            <QuickBtn to="/admin/intelligence" label="Intelligence commerciale" testid="admin-nav-intelligence" />
            <QuickBtn to="/admin/price-lists" label="Listini prezzi" testid="admin-nav-price-lists" />
            <QuickBtn to="/admin/carrier-tariffs" label="Tariffe Vettori" testid="admin-nav-carrier-tariffs" />
            <QuickBtn to="/admin/pricing-simulator" label="Pricing Simulator" testid="admin-nav-pricing" />
          </Card.Body>
        </Card>
        <Card>
          <Card.Header><strong>Sicurezza & controllo</strong></Card.Header>
          <Card.Body style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <QuickBtn to="/admin/rbac" label="Ruoli & Permessi" testid="admin-nav-rbac" />
            <QuickBtn to="/admin/audit" label="Audit Log" testid="admin-nav-audit" />
            <QuickBtn to="/admin/branding" label="Branding" testid="admin-nav-branding" />
            <QuickBtn to="/admin/imports" label="Import Center" testid="admin-nav-imports" />
            <QuickBtn to="/admin/order-imports" label="Ordini da importare" testid="admin-nav-order-imports" />
            <QuickBtn to="/admin/change-requests" label="Richieste anagrafiche" testid="admin-nav-change-requests" />
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <strong style={{ color: "#64748b" }}>Operatività secondaria</strong>
          </Card.Header>
          <Card.Body style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div className="muted" style={{ fontSize: ".8rem", marginBottom: 4 }}>
              Strumenti di supporto tecnico. L'operatività quotidiana avviene
              nei portali SALES e LOGISTICS.
            </div>
            <QuickBtn to="/admin/anomalies" label="Anomalie" testid="admin-nav-anomalies" />
            <QuickBtn to="/admin/outbox" label="Notification Outbox" testid="admin-nav-outbox" />
            <QuickBtn to="/admin/notifications-config" label="Notifiche Email" testid="admin-nav-notifications-config" />
            <QuickBtn to="/admin/announcements" label="Comunicazioni" testid="admin-nav-announcements" />
          </Card.Body>
        </Card>
      </div>
    </Section>
  );
}

// ---------- Anomalies view ----------

const ANOMALY_TYPES = [
  "PAYMENT_EXPIRED", "PAYMENT_DUE_SOON", "MISSING_DDT", "PARTIAL_FULFILLMENT",
  "DELIVERY_DATE_NOT_CONFIRMED", "UNSENT_LOAD_PLAN_CHANGES", "PRICING_CONFLICT",
  "PRODUCT_UNAVAILABLE", "ORDER_MODIFIED_AFTER_REVIEW",
];

export function AdminAnomaliesPage() {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [filters, setFilters] = useState({ types: [], severity_min: "", customer_id: "" });
  const [customerText, setCustomerText] = useState("");

  const load = async () => {
    setErr(null);
    try {
      const qp = new URLSearchParams();
      if (filters.types.length) qp.set("types", filters.types.join(","));
      if (filters.severity_min) qp.set("severity_min", filters.severity_min);
      if (filters.customer_id) qp.set("customer_id", filters.customer_id);
      const data = await apiGet(`/api/anomalies?${qp.toString()}`);
      setRows(data);
    } catch (e) { setErr(e?.message || String(e)); setRows([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleType = (t) => {
    const s = new Set(filters.types);
    if (s.has(t)) s.delete(t); else s.add(t);
    setFilters({ ...filters, types: Array.from(s) });
  };

  // Client-side filter by customer name text (server accepts id only)
  const filtered = customerText
    ? (rows || []).filter((r) => (r.customer_name || "").toLowerCase().includes(customerText.toLowerCase()))
    : rows;

  return (
    <Section title="Anomalie" subtitle="Rilevamento centralizzato — server-side"
             data-testid="admin-anomalies-page">
      <Card>
        <Card.Header>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <Label>Severity minima</Label>
              <select className="ds-input" value={filters.severity_min} data-testid="anom-severity"
                      onChange={(e) => setFilters({ ...filters, severity_min: e.target.value })}>
                <option value="">Tutte</option>
                <option value="INFO">INFO+</option>
                <option value="WARNING">WARNING+</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
            </div>
            <div style={{ minWidth: 220 }}>
              <Label>Cliente (nome)</Label>
              <Input value={customerText} data-testid="anom-customer"
                     placeholder="Filtra per cliente"
                     onChange={(e) => setCustomerText(e.target.value)} />
            </div>
            <Button onClick={load} variant="primary" data-testid="anom-apply">Applica filtri</Button>
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ANOMALY_TYPES.map((t) => (
              <button key={t} data-testid={`anom-type-${t}`}
                      onClick={() => toggleType(t)}
                      className="ds-btn ds-btn--sm"
                      style={{
                        background: filters.types.includes(t) ? "#0F4C81" : "#e9ecef",
                        color: filters.types.includes(t) ? "#fff" : "#333",
                      }}>
                {t}
              </button>
            ))}
          </div>
        </Card.Header>
        <Card.Body>
          {err && <Alert variant="danger">Errore: {err}</Alert>}
          {!rows && !err && <p className="muted">Caricamento…</p>}
          {filtered && filtered.length === 0 && <Alert tone="success">Nessuna anomalia rilevata con i filtri correnti.</Alert>}
          {filtered && filtered.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="anom-table">
                <thead><tr>
                  <th>Severity</th><th>Tipo</th><th>Titolo</th>
                  <th>Cliente</th><th>Ordine</th><th>Rilevato</th>
                </tr></thead>
                <tbody>
                  {filtered.map((a, i) => (
                    <tr key={i} data-testid={`anom-row-${a.anomaly_type}`}>
                      <td><Badge tone={SEVERITY_TONE[a.severity] || "neutral"}>{a.severity}</Badge></td>
                      <td><code style={{ fontSize: ".8rem" }}>{a.anomaly_type}</code></td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{a.title}</div>
                        <div className="muted" style={{ fontSize: ".85rem" }}>{a.description}</div>
                      </td>
                      <td>{a.customer_name || "—"}</td>
                      <td>{a.order_number || a.shipment_number || "—"}</td>
                      <td className="muted" style={{ fontSize: ".85rem" }}>{fmtDateTime(a.detected_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Section>
  );
}

// ---------- Outbox view ----------

export function AdminOutboxPage() {
  const { apiGet, apiPost } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setErr(null);
    try {
      const qp = new URLSearchParams();
      if (filter) qp.set("status", filter);
      const data = await apiGet(`/api/admin/notifications/outbox?${qp.toString()}&limit=200`);
      setRows(data);
    } catch (e) { setErr(e?.message || String(e)); setRows([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const retry = async (rowId) => {
    setNotice(null);
    try {
      await apiPost(`/api/admin/notifications/outbox/${rowId}/retry`, {});
      setNotice("Retry richiesto — la notifica verrà rispedita al prossimo tick.");
      await load();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  const tickNow = async () => {
    setNotice(null);
    try {
      await apiPost("/api/admin/notifications/outbox/tick", {});
      setNotice("Tick eseguito.");
      await load();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  const statusTone = { PENDING: "info", PROCESSING: "info", SENT: "success", FAILED: "danger", CANCELLED: "neutral" };

  return (
    <Section title="Notification Outbox" subtitle="Coda persistente email — retry manuale disponibile"
             data-testid="admin-outbox-page"
             actions={<Button onClick={tickNow} data-testid="outbox-tick-btn">Forza tick</Button>}>
      <Card>
        <Card.Header>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <Label>Stato</Label>
              <select className="ds-input" value={filter} data-testid="outbox-filter"
                      onChange={(e) => setFilter(e.target.value)}>
                <option value="">Tutti</option>
                <option value="PENDING">PENDING</option>
                <option value="PROCESSING">PROCESSING</option>
                <option value="SENT">SENT</option>
                <option value="FAILED">FAILED</option>
                <option value="CANCELLED">CANCELLED</option>
              </select>
            </div>
            <Button onClick={load} variant="primary">Aggiorna</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {notice && <Alert tone="success">{notice}</Alert>}
          {err && <Alert variant="danger">Errore: {err}</Alert>}
          {!rows && !err && <p className="muted">Caricamento…</p>}
          {rows && rows.length === 0 && <Alert tone="info">Nessuna notifica trovata con i filtri correnti.</Alert>}
          {rows && rows.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="outbox-table">
                <thead><tr>
                  <th>Stato</th><th>Evento</th><th>Destinatario</th><th>Oggetto</th>
                  <th>Tentativi</th><th>Ultimo errore</th><th>Creata</th><th>Inviata</th><th></th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`outbox-row-${r.id}`}>
                      <td><Badge tone={statusTone[r.status] || "neutral"}>{r.status}</Badge></td>
                      <td><code style={{ fontSize: ".8rem" }}>{r.event_type}</code></td>
                      <td style={{ fontSize: ".9rem" }}>{r.recipient}</td>
                      <td style={{ fontSize: ".9rem", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.subject}
                      </td>
                      <td>{r.attempt_count}/{r.max_attempts}</td>
                      <td style={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                   fontSize: ".8rem", color: "#a00" }}>
                        {r.last_error || "—"}
                      </td>
                      <td className="muted" style={{ fontSize: ".8rem" }}>{fmtDateTime(r.created_at)}</td>
                      <td className="muted" style={{ fontSize: ".8rem" }}>{fmtDateTime(r.sent_at)}</td>
                      <td>
                        {(r.status === "FAILED" || r.status === "PENDING") && (
                          <Button size="sm" onClick={() => retry(r.id)}
                                  data-testid={`outbox-retry-${r.id}`}>Retry</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Section>
  );
}
